import fetcher, { FleetFile } from '../fetcher';
import dayjs from 'dayjs';

export type CreditSimulationListParams = {
	pagination: {
		pageSize: number;
		pageNumber: number;
	};
	search?: {
		by: string;
		value: string;
	};
	spfId?: number;
};

export type CreditSimulationListItem = {
	simulationId: number;
	spfId: number;
	spfNo: string;
	mklNo: string;
	facilityId: number;
	simulationNo: string;
	brandName: string;
	dealerName: string;
	isEdit: boolean;
};

export type CreditSimulationListResponse = {
	totalCount: number;
	pageSize: number;
	currentPage: number;
	totalPages: number;
	items: CreditSimulationListItem[];
};

export const getCreditSimulationList = (params: CreditSimulationListParams) => {
	return fetcher<CreditSimulationListResponse>({
		api: '/credit-simulation/get-list',
		body: params,
	});
};

export type SearchFinancingLimitParams = {
	pagination: {
		pageSize: number;
		pageNumber: number;
	};
	search?: {
		by: string;
		value: string;
	};
	approvalDate?: string;
	financingLimitTypeId?: number;
	plafonTypeId?: number;
	expiredDate?: string;
	orderSourceId?: number;
};

export type SearchFinancingLimitItem = {
	spfId: number;
	facilityId: number;
	napResolutionId: number;
	spfNo: string;
	mklNo: string;
	customerId: string;
	customerName: string;
	bmppGroupId: string;
	bmppGroupName: string;
	approvalDate: string;
	existingFinancingLimit: number;
	financingLimitType: string;
	plafonType: string;
	expiredDate: string;
	orderSource: string;
	activityStatus: string;
};

export type SearchFinancingLimitResponse = {
	totalCount: number;
	pageSize: number;
	currentPage: number;
	totalPages: number;
	items: SearchFinancingLimitItem[];
};

export const searchFinancingLimit = (params: SearchFinancingLimitParams) => {
	return fetcher<SearchFinancingLimitResponse>({
		api: '/credit-simulation/search-financing-limit',
		body: params,
	});
};

export type GetSimulationFacilitiesParams = {
	pagination: {
		pageSize: number;
		pageNumber: number;
	};
	search?: {
		by: string;
		value: string;
	};
	spfId?: number;
};

export type SimulationFacilityItem = {
	spfId: number;
	facilityId: number;
	financingObject: string;
	financingObjectCondition: string;
	plafonType: string;
	financingLimitType?: string;
	tenor: number;
	interestRateIndicationPercent: number;
	downPaymentPercent: number;
	securityDeposit: number;
	administrativeFee: number;
	provisionFeePercent: number;
	serviceFeePercent: number;
	insurance: string;
	insuranceDiscountPercentage?: number;
	fiduciaryFee: number;
};

export type GetSimulationFacilitiesResponse = {
	totalCount: number;
	pageSize: number;
	currentPage: number;
	totalPages: number;
	items: SimulationFacilityItem[];
};

export const getSimulationFacilities = (
	params: GetSimulationFacilitiesParams,
) => {
	return fetcher<GetSimulationFacilitiesResponse>({
		api: '/credit-simulation/get-facilities',
		body: params,
	});
};

export type CalculateSimulationRequest = {
	isSplitPo: boolean;
	dealerSupplier: {
		id: string;
		name: string;
		phoneNo: string;
		address: string;
	};
	spfId: number;
	dealerSupplierId: string;
	dealerSupplierPicName: string;
	dealerSupplierPicPhoneNo: string;
	dealerSupplierPhoneNo: string;
	dealerSupplierAddress: string;
	dealerSupplierName: string;
	calculationTypeId: number;
	karoseriCompanyId: string;
	karoseriCompanyPicName: string;
	karoseriCompanyPicPhoneNo: string;
	karoseriCompanyPhoneNo: string;
	karoseriCompanyAddress: string;
	karoseriCompanyName: string;
	facilityId: number;
	assetConditionId: number;
	assetUsageId: number;
	assetCategoryId: number;
	assetBrandId: string;
	assetId: string;
	assetType: string;
	assetTypeId: string;
	assetManufactureYear: number;
	otrTypeId: number;
	financingProductId: number;
	productId: string;
	productOffering: string;
	installmentTypeId: number;
	karoseriCalculation: {
		type: string;
		bpkbOwnerName: string;
		unitPrice: string;
		downPaymentPercentage: string;
	};
	chasisCalculation: {
		type: string;
		bpkbOwnerName: string;
		unitPrice: string;
		downPaymentPercentage: string;
	};
	requestedOtrAmount: number;
	otrSubsidyAmount: number;
	downPaymentPercentage: number;
	effectiveRatePercentage: number;
	tenorInYear: number;
	totalUnit: number;
	provisionPayment: string;
	provisionPercentage: number;
	servicingFee: number;
	administrationPayment: string;
	administrationFee: number;
	fiduciaryPayment: string;
	fiduciaryFee: number;
	totalDownPaymentSubsidyAmount: number;

	isFlood: boolean;
	isEarthquake: boolean;
	isRiot: boolean;
	isTerrorism: boolean;
	isChasisAndKaroseri: boolean;
	otrChasisAmount: number;
	otrKaroseriAmount: number;
	isHeavyEquipmentAndOtherComponent: boolean;
	isBundlingFiduciaryFee: boolean;
	otrHeavyEquipmentAmount: number;
	otrOtherHeavyEquipmentComponentAmount: number;
	phJmbAmount: number;
	kjppLiquidityAmount: number;
	tjhAmount: number | string;
	driverPersonalAccident: number | string;
	passengerPersonalAccident: number | string;
	totalPassenger: number;
	isHic: boolean;
	isGap: boolean;

	// INSURANCE
	insurancePaymentId: number;
	insuranceRegion: number;
	insuranceRateLimitId: number;
	insuranceTenorTypeId: number;
	firstInsuranceType: string;
	secondInsuranceType: string;
	bankerClauseStartDate: dayjs.Dayjs;
	bankerClauseEndDate: dayjs.Dayjs;
	mtfClauseStartDate: dayjs.Dayjs;
	mtfClauseEndDate: dayjs.Dayjs;
	insurancePolicyDocument: FleetFile;
	insurancePaymentDocument: FleetFile;
	// Split PO
	paymentBreakdownPercentages: number[];
	// ghost keys for form display
	assetCategory: string;

	// New Fields from Update
	insuranceDiscountPercentage?: number;
	insurancePaymentFullCashTypeId?: number;
	insurancePaymentSchemeGiroTypeId?: number;
	insuranceGiroTenor?: number[];
};

export type SaveSimulationRequest = CalculateSimulationRequest & {
	spfId: number;
	facilityId: number;
};

export type CalculateSimulationResult = {
	customerId: string;
	customerName: string;
	spfId: number;
	facilityId: number;
	calculationTypeId: number;
	fixedCreditSimulation: {
		assetType: string;
		dealerSupplier: string;
		financingType: string;
		insuranceType: string;
		paymentType: string;
		tenorInMonth: number;
		downPaymentPercentage: number;
		interestPercentage: number;
		provisionPercentage: number;
		insurancePercentage: number;
		insuranceSchemes: {
			tenorInYear: number;
			description: string;
			coverageType: string;
			premiumAmount: number;
			discountAmount: number;
			paymentType: string;
		}[];

		creditSchemes: {
			unit: number;
			otrAmount: number;
			fullInsuranceAmount: number;
			downPaymentAmount: number;
			principalAmount: number;
			interestAmount: number;
			totalInstallmentReceivableAmount: number;
			totalInstallmentAmount: number;
			insuranceAmountDetails: {
				description: string;
				tenor: number;
				amount: number;
			}[];
		}[];

		firstPayments: {
			unit: number;
			downPaymentAmount: number;
			insuranceAmount: number;
			administrationAmount: number;
			fiduciaryFee: number;
			totalFee: number;
			provisionFee: number;
			servicingFee: number;
			firstInstallmentAmount: number;
			totalFirstPayment: number;
		}[];
	} | null;
	stepCreditSimulation: {
		lesseeName: string;
		lesseePicName: string;
		assetType: string;
		unit: number;
		transactionType: string;
		currency: string;
		effectiveRatePercentage: number;
		tenorInMonth: number;
		insuranceType: string;
		paymentCycle: string;
		outstandingPrincipalAmount: number;
		financingAmount: number;
		administrationFee: number;
		insuranceAmount: number;
		provisionFee: number;
		firstInstallmentAmount: number;
		firstPaymentAmount: number;
		netDownPaymentAmount: number;
		componentInstallments: {
			tenor: string;
			installmentPercentage: number;
			installmentAmount: number;
			installmentAmountInMaxUnit: number;
			unit: number;
		}[];

		installments: {
			tenorInMonth: number;
			installmentAmount: number;
			roundedInstallmentAmount: number;
			interestAmount: number;
			principalAmount: number;
			outstandingPrincipalAmount: number;
		}[];
	} | null;
	insuranceSchemes?: {
		tenorInYear: number;
		description: string;
		coverageType: string;
		premiumAmount: number;
		discountAmount: number;
		paymentType: string;
	}[];
	creditSchemes?: {
		unit: number;
		otrAmount: number;
		fullInsuranceAmount: number;
		downPaymentAmount: number;
		principalAmount: number;
		interestAmount: number;
		totalInstallmentReceivableAmount: number;
		totalInstallmentAmount: number;
		insuranceAmountDetails: {
			description: string;
			tenor: number;
			amount: number;
		}[];
	}[];
};

export type CalculateSimulationResponse = CalculateSimulationResult;

export const calculateSimulation = (body: CalculateSimulationRequest) => {
	return fetcher<CalculateSimulationResponse>({
		api: '/credit-simulation/calculate',
		body,
	});
};

export type GetCalculatedSimulationRequest = {
	simulationId?: number;
};

export const getCalculatedSimulation = (
	body: GetCalculatedSimulationRequest,
) => {
	return fetcher<CalculateSimulationResponse>({
		api: '/credit-simulation/get-result',
		body,
	});
};

export type GetSearchOptionsResponse = {
	searchByOptions: Array<{ id: string; value: string }>;
	plafonTypeOptions: Array<{ id: number; value: string }>;
	financingLimitTypeOptions: Array<{ id: number; value: string }>;
	orderSource: Array<{ id: number; value: string }>;
};

export const getSearchOptions = () => {
	return fetcher<GetSearchOptionsResponse>({
		api: '/credit-simulation/get-search-options',
		method: 'POST',
	});
};

export const getSimulationTodoCount = () => {
	return fetcher<number>({
		api: '/credit-simulation/get-things-todo',
		method: 'POST',
	});
};

export type GetSimulationDetailRequest = {
	spfId: number;
	facilityId: number;
	calculationTypeId: number;
};

export type OptionType = { id: number | string; value: string };

export type GetSimulationDetailResponse = {
	customerId: string;
	customerName: string;
	mklNo: string;
	bmppGroupId: string;
	bmppGroupName: string;
	orderSource: string;
	industryClass: string;
	portfolioGuideline: string;
	ultimateOwner: string;
	customerType: string;
	companyType: string;
	calculationType: OptionType;
	isSplitPo: boolean;
	dealerSupplier: {
		id: string;
		name: string;
		phoneNo: string;
		address: string;
	};
	dealerSupplierPicName: string;
	dealerSupplierPicPhoneNo: string;
	karoseriCompany: {
		id: string;
		name: string;
		phoneNo: string;
		address: string;
	};
	karoseriCompanyPicName: string;
	karoseriCompanyPicPhoneNo: string;
	assetType: string;
	assetTypeId: string;
	assetCondition: OptionType;
	assetUsage: OptionType;
	assetCategory: string;
	assetBrand: OptionType & { id: string };
	asset: OptionType & { id: string };
	assetManufactureYear: number;
	otrType: OptionType;
	financingProduct: OptionType;
	product: OptionType & { id: string };
	productOffering: string;
	paymentBreakdownPercentages: number[];
	installmentType: OptionType;
	karoseriCalculation: {
		type: string;
		bpkbOwnerName: string;
		unitPrice: number;
		totalPrice: number;
		downPaymentPercentage: number;
		downPaymentAmount: number;
	};
	chasisCalculation: {
		type: string;
		bpkbOwnerName: string;
		unitPrice: number;
		totalPrice: number;
		downPaymentPercentage: number;
		downPaymentAmount: number;
	};
	requestedOtrAmount: number;
	isHeavyEquipmentAndOtherComponent: boolean;
	otrSubsidyAmount: number;
	otrOtherHeavyEquipmentComponentAmount: number;
	otrHeavyEquipmentAmount: number;
	kjppLiquidityAmount: number;
	downPaymentPercentage: number;
	effectiveRatePercentage: number;
	tenorInYear: OptionType;
	totalUnit: number;
	provisionPayment: string;
	provisionFeePercentage: number;
	servicingFee: number;
	administrationPayment: string;
	administrationFee: number;
	fiduciaryPayment: string;
	fiduciaryFee: number;
	isBundlingFiduciaryFee: boolean;
	totalDownPaymentSubsidyAmount: number;
	// INSURANCE
	insuranceType: string;
	firstInsuranceType: string;
	secondInsuranceType: string;
	bankerClauseStartDate: string;
	bankerClauseEndDate: string;
	mtfClauseStartDate: string;
	mtfClauseEndDate: string;
	insurancePayment: OptionType;
	insuranceRegion: OptionType;
	insuranceRateLimit: OptionType;
	insuranceTenorType: OptionType;
	// CONDITIONS
	isFlood: boolean;
	isEarthquake: boolean;
	isRiot: boolean;
	isTerrorism: boolean;
	tjhAmount: OptionType;
	driverPersonalAccident: OptionType;
	passengerPersonalAccident: OptionType;
	totalPassenger: number;
	isHic: boolean;
	isGap: boolean;
	calculationTypeOptions: OptionType[];
	assetConditionOptions: OptionType[];
	assetUsageOptions: OptionType[];
	otrTypeOptions: OptionType[];
	financingProductOptions: OptionType[];
	installmentTypeOptions: OptionType[];
	tenorInYearOptions: OptionType[];
	insurancePaymentOptions: OptionType[];
	insuranceRegionOptions: OptionType[];
	insuranceRateLimitOptions: OptionType[];
	insuranceTenorTypeOptions: OptionType[];
	tjhAmountOptions: OptionType[];
	driverPersonalAccidentOptions: OptionType[];
	passengerPersonalAccidentOptions: OptionType[];
	passengerCountOptions: OptionType[];
	insurancePaymentDocument?: FleetFile;
	insurancePolicyDocument?: FleetFile;
	minimumDownPaymentPercentage: number;
	minimumEffectiveRatePercentage: number;
	minimumProvisionPercentage: number;
	minimumAdministrationFee: number;
	// Risalah
	resolutionFile: FleetFile;
	// NAP
	plafonTypeId: number;
	prospectId: string;
	// Additional fields from API contract
	isChasisAndKaroseri?: boolean;
	otrChasisAmount?: number;
	otrKaroseriAmount?: number;
	phJmbAmount?: number;

	// New Fields populated from API for Edit/View
	insuranceDiscountPercentage?: number;
	insurancePaymentFullCashTypeId?: number;
	insurancePaymentSchemeGiroTypeId?: number;
	insuranceGiroTenor?: number[];

	// New Fields
	paymentInsuranceFullCashOptions?: OptionType[];
	paymentSchemeInsuranceGiroOptions?: OptionType[];
	maximumInsuranceDiscountPercentage?: number;
};

export const getSimulationDetail = (body: GetSimulationDetailRequest) => {
	return fetcher<GetSimulationDetailResponse>({
		api: '/credit-simulation/get-detail',
		method: 'POST',
		body,
	});
};

export type GetSimulationHistoryDetailRequest = {
	simulationId: number;
};

export const getSimulationHistoryDetail = (
	body: GetSimulationHistoryDetailRequest,
) => {
	return fetcher<GetSimulationDetailResponse>({
		api: '/credit-simulation/get-history-detail',
		method: 'POST',
		body,
	});
};

/**
 * Calls the /api/credit-simulation/download endpoint to get a signed download URL for a simulation.
 * @param body - The request body containing the simulation id.
 * @returns The response with the signedUrl.
 */
export type DownloadSimulationRequest = {
	spfId: number;
	facilityId: number;
	simulationId: number;
};
export type DownloadSimulationResponse = { signedUrl: string };

export const downloadSimulation = (body: DownloadSimulationRequest) => {
	return fetcher<DownloadSimulationResponse>({
		api: '/credit-simulation/download',
		body,
		method: 'POST',
	});
};

export type SaveSimulationResponse = {
	simulationId: number;
	simulationNo: string;
};

export const saveSimulationCalculation = (body: CalculateSimulationRequest) => {
	return fetcher<SaveSimulationResponse>({
		api: '/credit-simulation/save',
		body,
		method: 'POST',
	});
};

export const editSimulationCalculation = (
	body: CalculateSimulationRequest & { simulationId: number },
) => {
	return fetcher<SaveSimulationResponse>({
		api: '/credit-simulation/edit',
		body,
		method: 'POST',
	});
};

export type SimulationEditProductRequest = {
	simulationId: number;
	productId: string;
	productOffering: string;
	assetId: string;
	assetTypeId: string;
	assetBrandId: string;
};

export type SimulationEditProductResponse = {
	message: string;
};

export const editSimulationProduct = (body: SimulationEditProductRequest) => {
	return fetcher<SimulationEditProductResponse>({
		api: '/credit-simulation/edit-product',
		body,
		method: 'POST',
	});
};

/**
 * Calls the /api/credit-simulation/calculate-fiduciary-fee endpoint to get the calculated fiduciary fee.
 * @param body - The request body containing isBundle, unit, and requestedOtrAmount.
 * @returns The calculated fee (number).
 */
export type CalculateFiduciaryFeeRequest = {
	isBundle: boolean;
	unit: number;
	requestedOtrAmount: number;
};

export type CalculateFiduciaryFeeResponse = { fiduciaryFee: number };

export const calculateFiduciaryFee = (body: CalculateFiduciaryFeeRequest) => {
	return fetcher<CalculateFiduciaryFeeResponse>({
		api: '/credit-simulation/calculate-fiduciary-fee',
		body,
		method: 'POST',
	});
};

/**
 * Calls the /api/credit-simulation/calculate-servicing-fee endpoint to get the calculated servicing fee.
 * @param body - The request body containing isBundle, unit, and requestedOtrAmount.
 * @returns The calculated fee (number).
 */
export type CalculateServicingFeeRequest = {
	provisionPercentage: number;
	requestedOtrAmount: number;
	otrSubsidyAmount: number;
	downPaymentPercentage: number;
};

export type CalculateServicingFeeResponse = { servicingFee: number };

export const calculateServicingFee = (body: CalculateServicingFeeRequest) => {
	return fetcher<CalculateServicingFeeResponse>({
		api: '/credit-simulation/calculate-servicing-fee',
		body,
		method: 'POST',
	});
};

/**
 * Calls the /api/credit-simulation/calculate-phjmb-fee endpoint to get the calculated phjmb fee.
 * @param body - The request body containing isBundle, unit, and requestedOtrAmount.
 * @returns The calculated fee (number).
 */
export type CalculatePHJMBAmountRequest = {
	assetId: string;
	assetManufactureYear: number;
};

export type CalculatePHJMBAmountResponse = { phjmbAmount: number };

export const calculatePHJMBAmount = (body: CalculatePHJMBAmountRequest) => {
	return fetcher<CalculatePHJMBAmountResponse>({
		api: '/credit-simulation/calculate-phjmb-amount',
		body,
		method: 'POST',
	});
};
